/**
 * GENERATED_ASSET — batch005 domestic adapter: binds the canonical recipes hush.cushion.patched.v1,
 * hush.cups.pair.v1 and hush.tide_window.quiet.v1 to the existing named templates cushion_roll, mismatched_cups
 * and tide_window through the current town-template owner (mirrors tidalCascade/adapter.ts).
 *
 * - Construction is the reviewed shared package (src/shared/assets/hushDomestic); art is unchanged.
 * - Projection is the reviewed `projectArtifact` (domestic-window/source/consumer.ts): one UNIFORM scale fitting the
 *   painted bounds in the frame with a 2-unit side / 2-unit top margin, translated so the painted base (source y 0,
 *   the bottom of the painted support stroke) lands exactly on the frame bottom. Asymmetric art keeps its offset.
 * - Palette: the 10 town keys map onto render-token roles exactly like HUSH_PROP_PALETTES / the fountain adapter;
 *   when the incoming palette IS nook_warm or coastal_salt the exact token palette is used (reviewed art).
 * - SVG def ids are scoped by template + content digest, so two palettes/recipes never share a definition.
 * - The tide window is static: its back/mid/front layers are flattened in order into one body.
 */
import { TOWN_PROP_TEMPLATES, TOWN_PROP_PALETTE_KEYS, type TownPropPalette } from '../../../../../shared/scene/townPropTemplates';
import { HUSH_PROP_PALETTES } from '../../../../../shared/scene/hushPropTemplates';
import {
  buildDomestic, validateRecipe, HUSH_DOMESTIC_TOKENS, HUSH_DOMESTIC_RECIPES, HUSH_DOMESTIC_GENERATOR_REVISION,
  type DomesticRecipe, type DraftArtifact, type ResolvedPalette, type Tokens, type VisualAnchor, type VisualBounds,
  type ProtectedVisualRegion, type Point2,
} from '../../../../../shared/assets/hushDomestic';
import { apply, box, corners, el, n, type Matrix } from '../../../../../shared/assets/hushDomestic/kit';
import { sha256, stableStringify } from '../tidalCascade/rng';

export const HUSH_DOMESTIC_ADAPTER_REVISION = 'hush-domestic.adapter/revision-01';

export type HushDomesticTemplate = 'tide_window' | 'cushion_roll' | 'mismatched_cups';

const TEMPLATE_GENERATOR: Readonly<Record<HushDomesticTemplate, DomesticRecipe['generatorId']>> = Object.freeze({
  cushion_roll: 'hush.cushion.v1',
  mismatched_cups: 'hush.cups.v1',
  tide_window: 'hush.tide_window.v1',
});
const DEFAULT_RECIPE: Readonly<Record<HushDomesticTemplate, DomesticRecipe>> = Object.freeze({
  cushion_roll: HUSH_DOMESTIC_RECIPES.cushion,
  mismatched_cups: HUSH_DOMESTIC_RECIPES.cups,
  tide_window: HUSH_DOMESTIC_RECIPES.tideWindow,
});

export function isHushDomesticTemplate(t: unknown): t is HushDomesticTemplate {
  return typeof t === 'string' && Object.prototype.hasOwnProperty.call(TEMPLATE_GENERATOR, t);
}
function assertTemplate(t: unknown): asserts t is HushDomesticTemplate {
  if (!isHushDomesticTemplate(t)) {
    throw new Error(`Unknown hush domestic template ${JSON.stringify(t)}. Known: ${Object.keys(TEMPLATE_GENERATOR).join(', ')}`);
  }
}

// ---------------------------------------------------------------- palette ---------------------------------------------
const hex = (c: number): string => '#' + c.toString(16).padStart(6, '0');
function checkPalette(p: unknown): asserts p is TownPropPalette {
  if (!p || typeof p !== 'object') throw new Error('hush domestic palette: expected an object of 10 RGB integers');
  for (const k of TOWN_PROP_PALETTE_KEYS) {
    const v = (p as Record<string, unknown>)[k];
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > 0xffffff) {
      throw new Error(`hush domestic palette.${k}: must be an integer RGB color 0..0xffffff (got ${String(v)})`);
    }
  }
}
function mix(a: string, b: string, t: number): string {
  const pa = Number.parseInt(a.slice(1), 16), pb = Number.parseInt(b.slice(1), 16);
  const ch = (s: number): number => Math.round(((pa >> s) & 0xff) * (1 - t) + ((pb >> s) & 0xff) * t);
  return hex((ch(16) << 16) | (ch(8) << 8) | ch(0));
}
function sameAs(p: TownPropPalette, named: Readonly<Record<string, string>>): boolean {
  return TOWN_PROP_PALETTE_KEYS.every((k) => hex(p[k]) === named[k].toLowerCase());
}

/**
 * Town keys -> render-token roles (HUSH_PROP_PALETTES mapping). Exact token palette when the input is one of the
 * published Hush palettes; otherwise the recipe's own token palette with the 10 mapped roles and derived shades.
 */
export function hushDomesticRoles(p: TownPropPalette, basePaletteId: DomesticRecipe['paletteId']): ResolvedPalette {
  checkPalette(p);
  if (sameAs(p, HUSH_PROP_PALETTES.nook_warm)) return HUSH_DOMESTIC_TOKENS.palettes.nook_warm;
  if (sameAs(p, HUSH_PROP_PALETTES.coastal_salt)) return HUSH_DOMESTIC_TOKENS.palettes.coastal_salt;
  const t = HUSH_DOMESTIC_TOKENS.palettes[basePaletteId];
  const [stone, iron, wood, roof] = [hex(p.stone), hex(p.iron), hex(p.wood), hex(p.roof)];
  return {
    ...t,
    outline: iron, outlineSoft: mix(iron, stone, 0.5),
    paperBase: hex(p.primary), paperShadow: hex(p.shade), paperLight: hex(p.glassCore), warmCore: hex(p.glass),
    woodBase: wood, woodLight: hex(p.woodEdge), woodShadow: mix(wood, iron, 0.4),
    clothBlue: roof, clothBlueLight: hex(p.roofEdge), clothBlueShadow: mix(roof, iron, 0.35),
    stoneBase: stone, stoneLight: mix(stone, hex(p.glassCore), 0.35), stoneShadow: mix(stone, iron, 0.35),
    // reed*, moss, salt, waterFar, waterLight keep their token values: the town palette has no such keys.
  };
}

// ---------------------------------------------------------------- projection (reviewed consumer.ts::projectArtifact) --
export interface Frame { readonly w: number; readonly h: number }
export function hushDomesticProjection(paint: VisualBounds, frame: Frame): Matrix {
  if (!Number.isFinite(frame.w) || !Number.isFinite(frame.h) || frame.w < 8 || frame.h < 8) throw new Error('invalid allocation frame');
  const b = paint, s = Math.min((frame.w - 4) / b.width, (frame.h - 2) / b.height), tx = 2 - b.x * s, ty = frame.h - Math.max(0, b.y + b.height) * s;
  return [s, 0, 0, s, tx, ty];
}

// ---------------------------------------------------------------- describe -------------------------------------------
export interface HushDomesticOptions {
  /** Override the canonical recipe (validated; must belong to the template's generator). Never mutated. */
  readonly recipe?: unknown;
}
export interface HushDomesticPlacement {
  readonly template: HushDomesticTemplate;
  readonly recipeId: string;
  readonly frame: Frame;
  readonly matrix: Matrix;
  readonly scope: string;
  readonly contentDigest: string;
  readonly body: string;
  /** Frame coordinates (same matrix as the paint). */
  readonly paintBounds: VisualBounds;
  readonly anchors: readonly VisualAnchor[];
  readonly protectedRegions: readonly ProtectedVisualRegion[];
  /** Painted base (source origin) in frame coordinates; y is exactly frame.h. */
  readonly contact: Point2;
  readonly layerIds: readonly string[];
}

function recipeFor(template: HushDomesticTemplate, opts: HushDomesticOptions): DomesticRecipe {
  if (opts.recipe === undefined) return DEFAULT_RECIPE[template];
  const r = validateRecipe(opts.recipe);
  if (r.generatorId !== TEMPLATE_GENERATOR[template]) throw new Error(`recipe ${r.id} (${r.generatorId}) cannot render template ${template}`);
  return r;
}
function tokensWith(paletteId: DomesticRecipe['paletteId'], roles: ResolvedPalette): Tokens {
  return { ...HUSH_DOMESTIC_TOKENS, palettes: { ...HUSH_DOMESTIC_TOKENS.palettes, [paletteId]: roles } };
}
/** Logical content identity: everything that changes pixels, never the SVG namespace. */
function contentDigest(template: HushDomesticTemplate, recipe: DomesticRecipe, roles: ResolvedPalette): string {
  return sha256(stableStringify({
    template, recipe, roles, style: HUSH_DOMESTIC_TOKENS.style, tokens: HUSH_DOMESTIC_TOKENS.revision,
    generator: HUSH_DOMESTIC_GENERATOR_REVISION, adapter: HUSH_DOMESTIC_ADAPTER_REVISION,
    frame: TOWN_PROP_TEMPLATES[template].frame, projection: 'paint-fit-base-to-frame-bottom',
  }));
}

const memo = new Map<string, HushDomesticPlacement>();
const MEMO_LIMIT = 32;

export function describeHushDomestic(template: HushDomesticTemplate, palette: TownPropPalette, opts: HushDomesticOptions = {}): HushDomesticPlacement {
  assertTemplate(template);
  const recipe = recipeFor(template, opts);
  const roles = hushDomesticRoles(palette, recipe.paletteId);
  const digest = contentDigest(template, recipe, roles);
  const hit = memo.get(digest);
  if (hit) return hit;
  const frame = TOWN_PROP_TEMPLATES[template].frame;
  const scope = `hdom_${template}_${digest.slice(0, 12)}`;
  const art: DraftArtifact = buildDomestic(recipe, tokensWith(recipe.paletteId, roles), scope);
  const m = hushDomesticProjection(art.paintBounds, frame);
  const inner = `<defs>${art.defs}</defs>${el('g', { transform: `matrix(${m.map(n).join(' ')})` }, art.body)}`;
  const tb = (b: VisualBounds): VisualBounds => box(corners(b).map((p) => apply(m, p)));
  const placement: HushDomesticPlacement = Object.freeze({
    template, recipeId: recipe.id, frame, matrix: m, scope, contentDigest: digest,
    body: `<g data-part="hush-domestic-${template}" data-recipe="${recipe.id}" data-integration="GENERATED_ASSET">${inner}</g>`,
    paintBounds: tb(art.paintBounds),
    anchors: art.anchors.map((a) => ({ ...a, point: apply(m, a.point) })),
    protectedRegions: art.protectedRegions.map((r) => ({ ...r, bounds: tb(r.bounds) })),
    contact: apply(m, art.origin.point),
    layerIds: art.layers.map((l) => l.id),
  });
  if (memo.size >= MEMO_LIMIT) memo.delete(memo.keys().next().value as string);
  memo.set(digest, placement);
  return placement;
}

/** Drop-in body for buildTownTemplateBody: markup in the template's frame coordinates (0..w x 0..h). */
export function buildHushDomesticBody(template: HushDomesticTemplate, palette: TownPropPalette, opts: HushDomesticOptions = {}): string {
  return describeHushDomestic(template, palette, opts).body;
}

/**
 * The canonical `__tpl_<template>_<10 colors>` town suffix (same formula as townTemplateTextureSuffix, restated
 * so this module never imports the game suffix owner and cannot recurse once that owner dispatches here).
 */
export function canonicalTownSuffix(template: string, palette: TownPropPalette): string {
  const colors = TOWN_PROP_PALETTE_KEYS.map((key) => palette[key].toString(16).padStart(6, '0')).join('-');
  return `__tpl_${template}_${colors}`;
}

/** Canonical town suffix + `_` + 16-hex digest of recipe/palette-roles/tokens/generator/adapter/frame. */
export function hushDomesticTextureSuffix(template: HushDomesticTemplate, palette: TownPropPalette, opts: HushDomesticOptions = {}): string {
  assertTemplate(template);
  const recipe = recipeFor(template, opts);
  const roles = hushDomesticRoles(palette, recipe.paletteId);
  return `${canonicalTownSuffix(template, palette)}_${contentDigest(template, recipe, roles).slice(0, 16)}`;
}
